<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { formatParams } from '@/lib/api.service'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faEye as fasEye } from '@fortawesome/free-solid-svg-icons'

import { createUserNotificationSetting, updateUserNotificationSetting } from '@/lib/api/user_notification_settings'
import { updateUser } from '@/lib/api/users'

//Components
import toggle from '@/components/ui/toggle.vue'
import SkeletonLoader from '@/components/ui/skeleton_loader.vue'

// I18N
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Stores
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useToastStore } from '@/stores/toast.js'

const authenticationStore = useAuthenticationStore()
const toastStore = useToastStore()

// Refs
const enableNotifications = ref(false)
const emailNotifications = ref({
  quotes: false,
  vehicle_information: false,
  accountSummary: false
})
const isSaving = ref(false)
const isSaved = ref(false)
const websiteNotifications = ref({
  quote_returned: JSON.parse(localStorage.getItem('websiteNotifications_quote_returned') ?? 'true'),
  quote_expiring: JSON.parse(localStorage.getItem('websiteNotifications_quote_expiring') ?? 'true'),
  quote_delayed: JSON.parse(localStorage.getItem('websiteNotifications_quote_delayed') ?? 'true')
})

//Queries
import { useIntelichekFetch } from '@/lib/intelichek-fetch'
import Notification_toggle from '@/views/settings/components/notification_toggle.vue'

const notificationSettingsUrl = computed(() => {
  const url = new URL(`${import.meta.env.VITE_BACKEND_URL}/api/v2/servicechek/user_notification_settings`)
  return url.href
})
const {
  isFetching: isFetchingNotificationSettings,
  data: notificationSettings
} = useIntelichekFetch(notificationSettingsUrl, { refetch: true }).json()

// Lifecycle
onMounted(async () => {
  if (authenticationStore.currentUser) {
    enableNotifications.value = !authenticationStore.currentUser.user.attributes.do_not_contact
  }
})

watch(notificationSettings, (settings) => {
  emailNotifications.value.quotes = settings.data.find((x) => x.notification_type === 'quotes' && x.notification_method === 'email')?.enabled ?? true
  emailNotifications.value.vehicle_information = settings.data.find((x) => x.notification_type === 'vehicle_information' && x.notification_method === 'email')?.enabled ?? true
  emailNotifications.value.accountSummary = settings.data.find((x) => x.notification_type === 'account_summary' && x.notification_method === 'email')?.enabled ?? true
})

watch(() => websiteNotifications.value.quote_returned, (val) => {
  localStorage.setItem('websiteNotifications_quote_returned', JSON.stringify(val))
})

watch(() => websiteNotifications.value.quote_expiring, (val) => {
  localStorage.setItem('websiteNotifications_quote_expiring', JSON.stringify(val))
})

watch(() => websiteNotifications.value.quote_delayed, (val) => {
  localStorage.setItem('websiteNotifications_quote_delayed', JSON.stringify(val))
})

watch(isSaved, (val) => {
  if(val){
    setTimeout(() => {
      isSaved.value = false
    }, 3000)
  }
})

watch(enableNotifications, (globalTurnOnCommunications) => {
  if (globalTurnOnCommunications === false) {
    toastStore.setToastsEnabled(false)
    emailNotifications.value.quotes = false
    emailNotifications.value.vehicle_information = false
    emailNotifications.value.accountSummary = false
    websiteNotifications.value.quote_returned = false
    websiteNotifications.value.quote_expiring = false
    websiteNotifications.value.quote_delayed = false
  }
})

async function updateNotificationSettings() {
  isSaving.value = true

  await Promise.all([
    updateUser(authenticationStore.currentUser.authenticatable_id, { do_not_contact: !enableNotifications.value }),
    updateSetting('quotes', 'email', emailNotifications.value.quotes),
    updateSetting('vehicle_information', 'email', emailNotifications.value.vehicle_information),
    updateSetting('account_summary', 'email', emailNotifications.value.accountSummary),
    new Promise(resolve => setTimeout(resolve, 300))
  ])

  isSaving.value = false
  toastStore.success(t('settings.notification_settings_section.settings_saved'))
}

async function updateSetting(type, method, value) {
  const serverValue = notificationSettings.value.data.find((x) => x.notification_type === type && x.notification_method === method)

  if (!serverValue) {
    const data = {
      notification_type: type,
      notification_method: method,
      enabled: value
    }
    await createUserNotificationSetting(data)
  } else if (serverValue.enabled !== value) {
    await updateUserNotificationSetting(serverValue.id, { enabled: value })
  }
}

</script>

<template>
  <div class="vh-screen flex flex-col max-w-xl">
    <div v-if="!isFetchingNotificationSettings">
      <div class="flex flex-row items-center mb-6 pt-8">
        <p class="font-semibold text-2xl">{{ t('settings.notification_settings_section.name') }}</p>
      </div>

    <div class="mb-8 flex flex-row items-center">
      <label class="flex flex-row items-center">
        <input type="checkbox" v-model="enableNotifications" class="w-6 h-6 mr-2" />
        {{ t('settings.notification_settings_section.enable_all_notifications') }}
      </label>
    </div>

    <div class="mb-6 py-2">
      <p class="font-semibold text-lg">{{ t('settings.notification_settings_section.website_notifications') }}</p>
      <p class="p-2 subtitle">{{ t('settings.notification_settings_section.website_notifications_subtext') }}</p>
    </div>

    <div class="mb-8 flex flex-row items-center justify-between">
      <span>{{ t('settings.notification_settings_section.website_popups') }}</span>
      <toggle v-model="toastStore.toastsEnabled" :disabled="!enableNotifications" @update:modelValue="(value) => { toastStore.setToastsEnabled(value); if (value) toastStore.success(t('settings.notification_settings_section.website_popups_enabled')) }" />
    </div>

    <div class="ml-4">
      <Notification_toggle notification-type="quote_returned_notifications"
                           v-model="websiteNotifications.quote_returned"
                           :disabled="!enableNotifications || !toastStore.toastsEnabled"></Notification_toggle>
      <Notification_toggle notification-type="quote_expiring_notifications"
                           v-model="websiteNotifications.quote_expiring"
                           :disabled="!enableNotifications || !toastStore.toastsEnabled"></Notification_toggle>
      <Notification_toggle notification-type="quote_delayed_notifications"
                           v-model="websiteNotifications.quote_delayed"
                           :disabled="!enableNotifications || !toastStore.toastsEnabled"></Notification_toggle>
    </div>

    <div class="flex flex-row items-center mb-6 py-2">
      <p class="font-semibold text-lg">{{ t('settings.notification_settings_section.email_notifications') }}</p>
    </div>

    <Notification_toggle notification-type="quote_notifications"
                         v-model="emailNotifications.quotes"
                         :disabled="!enableNotifications"></Notification_toggle>
    <Notification_toggle notification-type="vehicle_information_notifications"
                         v-model="emailNotifications.vehicle_information"
                         :disabled="!enableNotifications"></Notification_toggle>
    <Notification_toggle notification-type="account_summary_notifications"
                         v-model="emailNotifications.accountSummary"
                         :disabled="!enableNotifications"></Notification_toggle>


    <div class=" space-x-4">
      <div class="inline-flex items-center p-2 rounded-md font-regular save-changes cursor-pointer"
           @click="updateNotificationSettings">
              <span class="px-2 font-semibold">
                {{ t('settings.account_settings_section.save_changes_button') }}
              </span>
        <span class="pl-2" v-if="isSaving">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
      </div>
      <div class="inline-flex items-center p-2 rounded-md font-regular cancel-button cursor-pointer">
              <span class="px-2 font-semibold">
                {{ t('settings.account_settings_section.cancel_button') }}
              </span>
      </div>
    </div>


    </div>
    <div v-else class="p-8">
      <SkeletonLoader type="form" />
    </div>
  </div>
</template>


<style lang="scss" scoped>
@import '@/assets/styles/views/settings/notification_settings';
</style>
